import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './Login.css';

function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const { name, email, password, confirmPassword } = formData;

    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await register(email, name, password);
      setSuccess('Account created successfully! Redirecting...');
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-bg-decoration"></div>

      <div className="login-container" style={{ maxWidth: '460px' }}>
        {/* Header */}
        <div className="login-header">
          <div className="login-logo">🏥</div>
          <h1 className="login-title">Feelio</h1>
          <p className="login-subtitle">
            Child Emotion Learning Platform<br />
            <span className="login-subtitle-small">Practitioner Registration</span>
          </p>
        </div>

        {/* Card */}
        <div className="login-card">
          <h2 className="login-form-title">Create Account</h2>

          {/* Error */}
          {error && (
            <div className="login-error">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Success */}
          {success && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: '#f0fff4',
              border: '2px solid #9ae6b4',
              borderRadius: '14px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#276749',
            }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {/* Full Name */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                👤 Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                disabled={isLoading}
                autoComplete="name"
              />
            </div>

            {/* Username / License ID */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                🪪 Username / License ID
              </label>
              <input
                id="email"
                name="email"
                type="text"
                placeholder="Choose a username (e.g., dr.smith)"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                disabled={isLoading}
                autoComplete="username"
              />
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                🔒 Password
              </label>
              <div className="password-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 6 characters"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  style={{ paddingRight: '48px', width: '100%' }}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                🔒 Confirm Password
              </label>
              <div className="password-wrapper">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="form-input"
                  style={{ paddingRight: '48px', width: '100%' }}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirm(!showConfirm)}
                  title={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" className="login-btn" disabled={isLoading}>
              {isLoading ? '🔄 Creating account...' : '✨ Register'}
            </button>

            {/* Link to Login */}
            <p style={{
              textAlign: 'center',
              fontSize: '14px',
              color: '#4a5568',
              margin: '8px 0 0 0',
              fontWeight: '600',
            }}>
              Already have an account?{' '}
              <Link
                to="/login"
                style={{ color: '#1e40af', textDecoration: 'none', fontWeight: '800' }}
              >
                Sign In
              </Link>
            </p>
          </form>
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p>
            🔐 <strong>Authorized Practitioners Only:</strong> Accounts are for medical practitioners managing child emotion learning sessions.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
